import React, { useState, useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import useTitanApp from "../../hooks/useTitanApp";
import { useNavigate } from "react-router";
import { setPatientInfoTab } from "../../dispatchers/appDispatchers";
import {
  setConsultationId,
  setIsConsultationEnabled,
  setIsFinalSummary,
  setPatientNotes,
  setPatientTabSwitcher,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setSignatureDetails,
  setTranscript,
  setSnomedCodes,
} from "../../dispatchers/patientDispatchers";
import dayjs from "dayjs";
import Skeleton from "@mui/material/Skeleton";
import {
  setSelectedTemplate,
  setStreamedText,
} from "../../dispatchers/templateDispatchers";
import { getCompanyDocumentsByCompanyId } from "../../dispatchers/companyDocumentsDispatchers";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

function groupBy(array, keyGetter) {
  const map = new Map();
  array.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return Object.fromEntries(map);
}

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr);
  const options = {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

function Consultations() {
  const { appState, patientState } = useTitanApp();
  const { patientConsultations, showPatientConsultationsLoader } =
    patientState || {};
  const { currentPharmacyId, darkMode, companyId } = appState || {};
  const [selectedRange, setSelectedRange] = useState(null);
  const navigate = useNavigate();

  const handleArrowClick = (patientId, consultationData) => {
    setIsConsultationEnabled(true);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes === null ? false : true);
    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );

    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }
    setPatientNotes(
      consultationData?.notes === null ? "" : consultationData?.notes,
    );
    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationData?.consultationId}`,
    );

    setPatientInfoTab(1);
    setShowNHS111ReferralTab(false);
    if (setSelectedPatientConsultation != null) {
      setPatientTabSwitcher(consultationData?.preConsultationTemplate ? 1 : 0);
    }
  };

  const dateRanges = [
    { label: "All", value: "all" },
    { label: "Last 30 days", value: 30 },
    { label: "Last 20 days", value: 20 },
    { label: "Last 10 days", value: 10 },
  ];

  const filteredConsultations = useMemo(() => {
    if (!patientConsultations) return [];

    let result = [...patientConsultations];
    result = result.filter((c) => c.consultationStatus === 1);
    if (selectedRange && selectedRange !== "all") {
      const daysAgo = dayjs().subtract(selectedRange, "day");
      result = result.filter((c) => dayjs(c?.createdOn).isAfter(daysAgo));
    }

    // Sort by most recent first
    return result.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
  }, [patientConsultations, selectedRange]);

  const grouped = groupBy(filteredConsultations, (c) =>
    formatDateLabel(c?.createdOn),
  );

  return (
    <div className="db-consultation-wrapper">
      <div className="consultation-header">
        <div>
          <h3>Consultations ({filteredConsultations?.length})</h3>
          <p className="mt-1">
            {selectedRange && selectedRange !== "all"
              ? `Showing consultations from the last ${selectedRange} days`
              : "Showing all consultations."}
          </p>
        </div>
        <Dropdown
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.value)}
          options={dateRanges}
          placeholder="Select Range"
          className="dropdown"
          panelClassName="bucket-dropdown-panel"
        />
      </div>
      {showPatientConsultationsLoader ? (
        <div className="consultation-skeleton-wrapper">
          {[...Array(3)].map((_, i) => (
            <Skeleton
              key={i}
              sx={{
                bgcolor: darkMode ? "grey.800" : "",
                width: "100%",
                height: "70px",
                borderRadius: "16px",
                marginBottom: "10px",
              }}
              variant="rounded"
            />
          ))}
        </div>
      ) : (
        <div className="consultation-list">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date} className="consultation-date-group">
              <div className="consultation-date">
                <p>{date}</p>
              </div>
              {items?.map((item) => (
                <div className="consultation-card" key={item?._id}>
                  <div className="consultation-info">
                    <div className="consult-title">
                      <p>{item?.templateName}</p>
                    </div>
                    <div className="consult-subtitle">{item?.serviceName}</div>
                  </div>
                  <div
                    className="consult-arrow"
                    onClick={() => handleArrowClick(item?.patientId, item)}
                    style={{ cursor: "pointer" }}
                  >
                    →
                  </div>
                </div>
              ))}
            </div>
          ))}
          {filteredConsultations.length === 0 && (
            <p style={{ padding: "10px" }}>No consultations in this range.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Consultations;
